import React, { useState, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ServiceProvider, Subscription, Location, City } from '@prisma/client';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { prisma } from '../db';
import { localeFiles } from '../data/constants';
import { Tier, tiers } from '../constants/service-provider-prices';
import { ENVIRONMENT } from '../env.server';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '../components/atoms/Button';
import ServiceProviderPackage from '../components/organisms/ServiceProviderPackage';
import PageDefault from '../components/templates/PageDefault';
import UpgradeHelper from '../components/molecules/UpgradeHelper';
import Prompt from '../components/molecules/Prompt';
import { serializePrismaObject } from '../lib/utils';

interface PageProps {
  serviceProvider?: ServiceProvider & {
    subscriptions: Subscription[];
    location: Location;
    cities: City[];
  };
  prices: Tier[];
}

const Page = ({ serviceProvider, prices }: PageProps) => {
  const router = useRouter();
  const [layer, setLayer] = useState() as any;
  const { t } = useTranslation(['common', 'pages']);
  const slug = 'service-provider-package';

  useEffect(() => {
    if (!!router.query.rfp_prompt) {
      setLayer(
        <Prompt onClick={() => setLayer({})}>
          <p>{t(`pages:${slug}:upgrade:rfp-prompt`)}</p>
        </Prompt>
      );
    }
  }, [serviceProvider, router, t]);

  return (
    <>
      <Head>
        <title>
          {t(`pages:${slug}:title`)} | {t('app-name')}
        </title>
        <meta name="description" content={t(`pages:${slug}:description`)} />
      </Head>

      <PageDefault layer={layer}>
        <Row>
          <Col>
            <Button
              onClick={() => router.back()}
              text
              iconLeft
              label={t('common:cta-back')}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <ServiceProviderPackage
              setLayer={setLayer}
              serviceProvider={serviceProvider}
              prices={prices || []}
            />
          </Col>
        </Row>
      </PageDefault>
    </>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query } = context;

  const baseProps = {
    ...(await serverSideTranslations(locale!, localeFiles)),
    prices: tiers[ENVIRONMENT],
  };

  const id = query.service_provider_id ? String(query.service_provider_id) : '';

  if (!id) {
    return { props: baseProps };
  }

  const serviceProvider = await prisma.serviceProvider.findUnique({
    where: {
      id,
    },
    include: {
      location: true,
      cities: true,
    },
  });

  if (!serviceProvider) {
    return { props: baseProps };
  }

  const prices = tiers[ENVIRONMENT]?.filter((tier) => tier.type === 'service');

  // const citiesCnt = serviceProvider.cities?.length || 0;
  // const prices = tiers[ENVIRONMENT]?.filter(
  //   (tier) =>
  //     tier.name ===
  //     (citiesCnt > 20
  //       ? 'thirtycities'
  //       : citiesCnt > 10
  //       ? 'twentycities'
  //       : citiesCnt > 5
  //       ? 'tencities'
  //       : citiesCnt > 1
  //       ? 'fivecities'
  //       : 'onectiy')
  // );

  return {
    props: {
      ...baseProps,
      serviceProvider: serializePrismaObject(serviceProvider),
      prices: prices,
    },
  };
}

export default Page;
